import { Button, Header, IHeaderKebabItem, Input } from '@ws/shared/components';
import { TEntityRef } from '@ws/shared/types';
import { Sync20, Type20, UploadCloud20, List20 } from '@ws/shared/icons';
import { compileCollection } from '@ws/shared/utils';
import { useCallback, useContext, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ResolutionContext, EditabilityContext } from '../../context';
import { isDesktop, isTablet } from '../../modules/getResolution';
import { TAppState } from '../../redux';
import {
  setGlobalIsCompiled,
  setGlobalIsPreviewsOpen,
  setGlobalIsTkPanelOpen,
} from '../../redux/general/general.actions';
import { exportDocx, exportPdf } from '../../utils/export';
import { useDebouncedNameSave } from '../../hooks/useDebouncedNameSave';
import { transliterate } from '../../utils/transliterate';

import { BeforeActions } from './BeforeActions';

export function HeaderContainer() {
  const dispatch = useDispatch();

  const { isCompiled } = useSelector((state: TAppState) => state.general);
  const { isAuth } = useSelector((state: TAppState) => state.user);
  const { resolution } = useContext(ResolutionContext);
  const { getNodeByRef, getNodesByRefs, updateNodeMeta, updateProjectMeta } =
    useContext(EditabilityContext);

  const { selected, project } = useSelector((state: TAppState) => state.editor);

  const currentNode = useMemo(
    () => (selected ? getNodeByRef(selected) : selected),
    [getNodeByRef, selected],
  );

  const tabletOrDesktop = isTablet(resolution) || isDesktop(resolution);
  const isWithTitle = currentNode?.meta.withTitle || false;
  const nodeType = currentNode?.type;
  const isCollection = nodeType === 'collection';

  const handleCompilationClick = useCallback(
    () => dispatch(setGlobalIsCompiled({ isCompiled: !isCompiled })),
    [dispatch, isCompiled],
  );

  const handleNameSave = useCallback(
    (nameToSave: string, owner: TEntityRef) => {
      updateNodeMeta(owner, { name: nameToSave });

      if (owner.id === project?.root) {
        updateProjectMeta({ name: nameToSave, slug: transliterate(nameToSave) });
      }
    },
    [updateNodeMeta, updateProjectMeta, project?.root],
  );

  const {
    name,
    onChange: handleNameChange,
    onBlur: handleNameInputBlur,
  } = useDebouncedNameSave({
    owner: selected,
    currentName: currentNode?.meta.name || '',
    onSave: handleNameSave,
  });

  // Данные собираются внутри экспорта: так падение компиляции попадает в ту же
  // границу ошибок, что и сборка файла, и не проходит мимо тоста.
  const getEntitiesForExport = useCallback(() => {
    if (!currentNode || currentNode.type !== 'collection') {
      return [];
    }

    return compileCollection({ collection: currentNode, getNodesByRefs });
  }, [currentNode, getNodesByRefs]);

  const handleDocxExport = useCallback(() => {
    void exportDocx(getEntitiesForExport, name.trim());
  }, [getEntitiesForExport, name]);

  const handlePdfExport = useCallback(() => {
    void exportPdf(getEntitiesForExport, name.trim());
  }, [getEntitiesForExport, name]);

  const handlePreviewsClick = useCallback(
    () => dispatch(setGlobalIsPreviewsOpen({ isPreviewsOpen: true })),
    [dispatch],
  );

  const handleTkPanelClick = useCallback(
    () => dispatch(setGlobalIsTkPanelOpen({ isTkPanelOpen: true })),
    [dispatch],
  );

  const handleWithTitleChange = useCallback(() => {
    if (selected && currentNode !== null && typeof currentNode !== 'undefined') {
      const checked = !currentNode.meta.withTitle;

      updateNodeMeta(selected, { withTitle: checked });
    }
  }, [currentNode, selected, updateNodeMeta]);

  const kebabMenu = useMemo<IHeaderKebabItem[]>(
    () => [
      {
        leftIcon: <Type20 />,
        label: isWithTitle ? 'Убрать заголовок' : 'Отобразить заголовок',
        onClick: handleWithTitleChange,
      },
      {
        leftIcon: <UploadCloud20 />,
        label: 'Предпоказы',
        onClick: handlePreviewsClick,
        disabled: !isAuth,
        closeAfterClick: true,
      },
      {
        // Без счётчика в подписи намеренно: он означал бы обход всего поддерева
        // на каждое изменение проекта, даже когда панель закрыта.
        leftIcon: 'TK',
        label: 'Метки TK',
        onClick: handleTkPanelClick,
        closeAfterClick: true,
      },
      {
        label: 'Скачать .pdf',
        onClick: handlePdfExport,
        // Как и docx, экспорт больше не читает DOM — режим Ленты ему не нужен.
        disabled: !isCollection,
        closeAfterClick: true,
      },
      {
        label: 'Скачать .docx',
        onClick: handleDocxExport,
        // Экспорт больше не читает DOM, поэтому режим Ленты ему не нужен.
        disabled: !isCollection,
        closeAfterClick: true,
      },
    ],
    // name и isCompiled сюда больше не входят: их захватывают обработчики экспорта,
    // а доступность пунктов теперь зависит только от того, коллекция ли это.
    [
      handleDocxExport,
      handlePdfExport,
      handlePreviewsClick,
      handleTkPanelClick,
      handleWithTitleChange,
      isAuth,
      isWithTitle,
      isCollection,
    ],
  );

  if (!selected || !currentNode) {
    return null;
  }

  return (
    <Header
      title={
        <Input
          variant="shapeless"
          value={name}
          onChange={handleNameChange}
          onBlur={handleNameInputBlur}
        />
      }
      before={<BeforeActions />}
      after={
        isCollection &&
        tabletOrDesktop && (
          <Button
            color={isCompiled ? 'secondary' : 'primary'}
            onClick={handleCompilationClick}
            icon={isCompiled ? <List20 /> : <Sync20 />}
          >
            <span style={{ whiteSpace: 'nowrap' }}>{isCompiled ? 'В список' : 'В ленту'}</span>
          </Button>
        )
      }
      kebab={kebabMenu}
    />
  );
}
